import { useState, useRef, useEffect } from 'react'
import { uploadFile, createContent, createSpace, listSpaces } from '../lib/api'
import {
  X,
  FolderUp,
  FileText,
  File,
  Image as ImageIcon,
  Mic,
  BookOpen,
  FileSpreadsheet,
  Presentation,
  Loader2,
  Check,
  AlertCircle,
  Trash2,
  Globe,
  Folder,
  FolderPlus,
} from 'lucide-react'

// File extension to content type mapping
const FILE_TYPE_MAP = {
  // Markdown/Text -> Note type
  md: 'note',
  markdown: 'note',
  txt: 'txt',

  // Documents
  pdf: 'pdf',
  doc: 'docx',
  docx: 'docx',
  xls: 'xlsx',
  xlsx: 'xlsx',
  ppt: 'pptx',
  pptx: 'pptx',
  epub: 'epub',

  // Images
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  heic: 'image',

  // Audio
  mp3: 'audio',
  wav: 'audio',
  m4a: 'audio',
  aac: 'audio',
  ogg: 'audio',

  // Other text formats
  json: 'txt',
  csv: 'txt',
  xml: 'txt',
  html: 'txt',
}

// Get icon for file type
function getFileIcon(type) {
  switch (type) {
    case 'note':
    case 'txt':
      return FileText
    case 'pdf':
    case 'docx':
      return FileText
    case 'xlsx':
      return FileSpreadsheet
    case 'pptx':
      return Presentation
    case 'epub':
      return BookOpen
    case 'image':
      return ImageIcon
    case 'audio':
      return Mic
    default:
      return File
  }
}

// Get color for file type
function getTypeColor(type) {
  switch (type) {
    case 'note':
      return '#8B5CF6'
    case 'txt':
      return '#6B7280'
    case 'pdf':
      return '#EF4444'
    case 'docx':
      return '#2563EB'
    case 'xlsx':
      return '#22C55E'
    case 'pptx':
      return '#F97316'
    case 'epub':
      return '#A855F7'
    case 'image':
      return '#EC4899'
    case 'audio':
      return '#14B8A6'
    default:
      return '#78716C'
  }
}

// Detect file type from extension
function detectFileType(filename) {
  const ext = filename.split('.').pop()?.toLowerCase()
  return FILE_TYPE_MAP[ext] || 'file'
}

export function BulkImportModal({ isOpen, onClose, onSuccess, defaultSpaceId }) {
  const [files, setFiles] = useState([])
  const [folders, setFolders] = useState([]) // Detected folders from file paths
  const [existingSpaces, setExistingSpaces] = useState([]) // Existing space names
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [error, setError] = useState(null)
  const [createdSpaces, setCreatedSpaces] = useState({}) // Map of folder name -> created space ID
  const folderInputRef = useRef(null)
  const fileInputRef = useRef(null)

  // Load existing spaces when modal opens
  useEffect(() => {
    if (isOpen) {
      loadExistingSpaces()
    }
  }, [isOpen])

  const loadExistingSpaces = async () => {
    try {
      const response = await listSpaces()
      const spaces = response.spaces || []
      // Flatten space names for comparison
      const spaceNames = flattenSpaceNames(spaces)
      setExistingSpaces(spaceNames)
    } catch (err) {
      console.error('Failed to load spaces:', err)
    }
  }

  // Recursively flatten space names
  const flattenSpaceNames = (spaces, result = []) => {
    for (const space of spaces) {
      result.push(space.name?.toLowerCase())
      if (space.children && space.children.length > 0) {
        flattenSpaceNames(space.children, result)
      }
    }
    return result
  }

  // Generate unique space name with suffix if needed
  const generateUniqueSpaceName = (baseName, existingNames) => {
    const lowerBaseName = baseName.toLowerCase()
    if (!existingNames.includes(lowerBaseName)) {
      return baseName
    }

    let suffix = 1
    while (existingNames.includes(`${lowerBaseName} (${suffix})`)) {
      suffix++
    }
    return `${baseName} (${suffix})`
  }

  const resetState = () => {
    setFiles([])
    setFolders([])
    setUploading(false)
    setUploadProgress({})
    setError(null)
    setCreatedSpaces({})
  }

  const handleClose = () => {
    if (uploading) return // Prevent closing while uploading
    resetState()
    onClose()
  }

  // Extract folder name from relative path (first folder after root)
  const getFolderFromPath = (relativePath) => {
    const parts = relativePath.split('/')
    // parts[0] is the root folder selected, parts[1] would be subfolder
    // If there's only root/file.txt, return null (file is in root)
    // If there's root/subfolder/file.txt, return subfolder
    if (parts.length >= 3) {
      return parts[1] // Return the first subfolder
    }
    return null // File is directly in the root folder
  }

  // Handle folder selection
  const handleFolderSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length === 0) return

    // Filter out system files and directories
    const validFiles = selectedFiles.filter(file => {
      const name = file.name.toLowerCase()
      // Skip hidden files and system files
      if (name.startsWith('.')) return false
      if (name === 'thumbs.db' || name === 'desktop.ini') return false
      if (name === '.ds_store') return false
      // Skip files in hidden folders
      const path = file.webkitRelativePath || ''
      if (path.split('/').some(part => part.startsWith('.'))) return false
      return true
    })

    // Detect unique subfolders
    const detectedFolders = new Set()
    validFiles.forEach(file => {
      const folder = getFolderFromPath(file.webkitRelativePath || '')
      if (folder) {
        detectedFolders.add(folder)
      }
    })

    // Create folder info with unique names
    const allExistingNames = [...existingSpaces, ...folders.map(f => f.name.toLowerCase())]
    const newFolders = Array.from(detectedFolders).map(folderName => {
      const uniqueName = generateUniqueSpaceName(folderName, allExistingNames)
      allExistingNames.push(uniqueName.toLowerCase()) // Add to list for next iteration
      return {
        originalName: folderName,
        name: uniqueName,
        willCreateSpace: true,
        status: 'pending', // pending | creating | success | error
      }
    })

    const newFiles = validFiles.map(file => {
      const folder = getFolderFromPath(file.webkitRelativePath || '')
      return {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: file.size,
        type: detectFileType(file.name),
        relativePath: file.webkitRelativePath || file.name,
        folder: folder, // Which folder this file belongs to (null if root)
        status: 'pending', // pending | uploading | success | error
        error: null,
      }
    })

    setFolders(prev => [...prev, ...newFolders])
    setFiles(prev => [...prev, ...newFiles])
    setError(null)
  }

  // Handle individual file selection
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length === 0) return

    const newFiles = selectedFiles.map(file => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
      type: detectFileType(file.name),
      relativePath: file.name,
      status: 'pending',
      error: null,
    }))

    setFiles(prev => [...prev, ...newFiles])
    setError(null)
  }

  // Remove a file from the list
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Upload all files
  const handleUpload = async () => {
    if (files.length === 0) return

    setUploading(true)
    setError(null)

    // Step 1: Create spaces for detected folders
    const spaceMap = { ...createdSpaces } // Map folder originalName -> spaceId

    for (const folder of folders) {
      if (folder.status === 'success' && spaceMap[folder.originalName]) {
        continue // Already created
      }

      // Update folder status to creating
      setFolders(prev => prev.map(f =>
        f.originalName === folder.originalName ? { ...f, status: 'creating' } : f
      ))

      try {
        const result = await createSpace(folder.name, defaultSpaceId)
        const newSpaceId = result.space?.space_id || result.space_id || result.id

        if (newSpaceId) {
          spaceMap[folder.originalName] = newSpaceId
          setCreatedSpaces(prev => ({ ...prev, [folder.originalName]: newSpaceId }))
        }

        // Mark folder as success
        setFolders(prev => prev.map(f =>
          f.originalName === folder.originalName ? { ...f, status: 'success' } : f
        ))
      } catch (err) {
        console.error(`Failed to create space ${folder.name}:`, err)
        // Mark folder as error
        setFolders(prev => prev.map(f =>
          f.originalName === folder.originalName ? { ...f, status: 'error' } : f
        ))
      }
    }

    // Step 2: Upload files to appropriate spaces
    let successCount = 0
    let errorCount = 0

    for (const fileItem of files) {
      if (fileItem.status === 'success') {
        successCount++
        continue
      }

      // Determine target space - use created space if file belongs to a folder
      let targetSpaceId = defaultSpaceId
      if (fileItem.folder && spaceMap[fileItem.folder]) {
        targetSpaceId = spaceMap[fileItem.folder]
      }

      // Update status to uploading
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'uploading' } : f
      ))

      try {
        if (fileItem.type === 'note') {
          // For markdown files, read content and create as note
          const content = await readFileAsText(fileItem.file)
          const title = fileItem.name.replace(/\.(md|markdown)$/i, '')

          await createContent({
            title,
            content,
            spaceId: targetSpaceId,
            type: 'note',
          })
        } else {
          // For other files, upload directly
          await uploadFile({
            file: fileItem.file,
            title: fileItem.name,
            spaceId: targetSpaceId,
            type: fileItem.type,
          })
        }

        // Mark as success
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'success' } : f
        ))
        successCount++
      } catch (err) {
        console.error(`Failed to upload ${fileItem.name}:`, err)

        // Mark as error
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'error', error: err.message } : f
        ))
        errorCount++
      }
    }

    setUploading(false)

    if (errorCount === 0) {
      // All successful
      onSuccess?.()
      handleClose()
    } else if (successCount > 0) {
      // Partial success
      setError(`${successCount} files imported, ${errorCount} failed.`)
      onSuccess?.() // Still refresh the list
    } else {
      // All failed
      setError('All imports failed. Please try again.')
    }
  }

  // Read file as text (for markdown files)
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  }

  // Count files by status
  const pendingCount = files.filter(f => f.status === 'pending').length
  const uploadingCount = files.filter(f => f.status === 'uploading').length
  const successCount = files.filter(f => f.status === 'success').length
  const errorCount = files.filter(f => f.status === 'error').length

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[600px] md:max-h-[80vh] bg-card rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <FolderUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Bulk Import</h2>
              <p className="text-xs text-muted-foreground">
                Import multiple files or an entire folder
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="p-1 rounded-lg hover:bg-accent disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {files.length === 0 ? (
            // Empty state - show upload options
            <div className="space-y-4">
              <div
                onClick={() => folderInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-accent/30 transition-all"
              >
                <FolderUp className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">Select a Folder</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Subfolders will be created as Spaces automatically
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-card px-3 text-sm text-muted-foreground">or</span>
                </div>
              </div>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 rounded-xl border border-border hover:border-primary/50 hover:bg-accent/30 transition-all text-sm font-medium"
              >
                Select Multiple Files
              </button>

              <div className="p-3 rounded-xl bg-muted/50 text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-2">Supported file types:</p>
                <ul className="grid grid-cols-2 gap-1 text-xs">
                  <li>• Markdown (.md) → Notes</li>
                  <li>• PDF documents</li>
                  <li>• Word (.doc, .docx)</li>
                  <li>• Excel (.xls, .xlsx)</li>
                  <li>• PowerPoint (.ppt, .pptx)</li>
                  <li>• eBooks (.epub)</li>
                  <li>• Images (jpg, png, gif...)</li>
                  <li>• Audio (mp3, wav, m4a...)</li>
                </ul>
              </div>
            </div>
          ) : (
            // File list
            <div className="space-y-2">
              {/* Stats */}
              <div className="flex items-center gap-4 text-sm mb-4 flex-wrap">
                <span className="text-muted-foreground">
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </span>
                {folders.length > 0 && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <FolderPlus className="w-3.5 h-3.5" />
                    {folders.length} space{folders.length !== 1 ? 's' : ''} to create
                  </span>
                )}
                {successCount > 0 && (
                  <span className="text-green-600 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" />
                    {successCount} imported
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errorCount} failed
                  </span>
                )}
              </div>

              {/* Folders to be created */}
              {folders.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <FolderPlus className="w-3.5 h-3.5" />
                    Spaces to be created:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {folders.map(folder => (
                      <div
                        key={folder.originalName}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                          folder.status === 'success'
                            ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                            : folder.status === 'error'
                            ? 'bg-destructive/10 text-destructive'
                            : folder.status === 'creating'
                            ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                            : 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                        }`}
                      >
                        {folder.status === 'creating' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : folder.status === 'success' ? (
                          <Check className="w-3.5 h-3.5" />
                        ) : folder.status === 'error' ? (
                          <AlertCircle className="w-3.5 h-3.5" />
                        ) : (
                          <Folder className="w-3.5 h-3.5" />
                        )}
                        <span className="font-medium">{folder.name}</span>
                        {folder.originalName !== folder.name && (
                          <span className="text-xs opacity-70">(renamed)</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File list */}
              <div className="max-h-[300px] overflow-auto space-y-1">
                {files.map(fileItem => {
                  const Icon = getFileIcon(fileItem.type)
                  const color = getTypeColor(fileItem.type)
                  const folderInfo = fileItem.folder ? folders.find(f => f.originalName === fileItem.folder) : null

                  return (
                    <div
                      key={fileItem.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border ${
                        fileItem.status === 'success'
                          ? 'bg-green-500/5 border-green-500/30'
                          : fileItem.status === 'error'
                          ? 'bg-destructive/5 border-destructive/30'
                          : 'bg-muted/30 border-border'
                      }`}
                    >
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color }} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{fileItem.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatSize(fileItem.size)}
                          {fileItem.type === 'note' && ' • Note'}
                          {folderInfo && (
                            <span className="text-amber-600 ml-2">→ {folderInfo.name}</span>
                          )}
                          {fileItem.error && (
                            <span className="text-destructive ml-2">{fileItem.error}</span>
                          )}
                        </p>
                      </div>

                      {fileItem.status === 'pending' && !uploading && (
                        <button
                          onClick={() => removeFile(fileItem.id)}
                          className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}

                      {fileItem.status === 'uploading' && (
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      )}

                      {fileItem.status === 'success' && (
                        <Check className="w-4 h-4 text-green-600" />
                      )}

                      {fileItem.status === 'error' && (
                        <AlertCircle className="w-4 h-4 text-destructive" />
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Add more files */}
              {!uploading && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => folderInputRef.current?.click()}
                    className="flex-1 py-2 rounded-lg border border-dashed border-border hover:border-primary/50 text-sm text-muted-foreground hover:text-foreground transition-all"
                  >
                    + Add Folder
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 py-2 rounded-lg border border-dashed border-border hover:border-primary/50 text-sm text-muted-foreground hover:text-foreground transition-all"
                  >
                    + Add Files
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {files.length > 0 && (
          <div className="p-4 border-t border-border flex justify-between items-center">
            <button
              onClick={resetState}
              disabled={uploading}
              className="px-4 py-2 rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
            >
              Clear All
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                disabled={uploading}
                className="px-4 py-2 rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || pendingCount === 0}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    Import {pendingCount} File{pendingCount !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Hidden inputs */}
        <input
          ref={folderInputRef}
          type="file"
          className="hidden"
          webkitdirectory=""
          directory=""
          multiple
          onChange={handleFolderSelect}
        />
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleFileSelect}
        />
      </div>
    </>
  )
}
